import { useContext, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { slugify } from '../utils/url';

import LocaleContext from '../contexts/locale';

export const useUseCaseByUuid = (uuid) => {
	const { uuidMap } = useUseCases();

	if (uuid in uuidMap) {
		return uuidMap[uuid];
	}

	return null;
};

export const useUseCaseBySlug = (slug) => {
	const { slugMap } = useUseCases();
	return slugMap[slug] || null;
};

export const useUseCases = () => {
	const {
		allMarkdownRemark: { edges: useCases },
	} = useStaticQuery(query);

	const allRef = useRef([]);
	const uuidMapRef = useRef({});
	const slugMapRef = useRef({});
	const localeMapRef = useRef({});

	const { currentLocale: locale } = useContext(LocaleContext);

	const mapItem = ({ node: { fields, frontmatter: data } }) => ({
		...data,
		...fields,
		...{
			slug: slugify(data.name),
		},
	});

	if (allRef.current.length === 0) {
		for (let useCase of useCases) {
			const item = mapItem(useCase);
			const { slug, uuid, locale } = item;

			localeMapRef.current[locale] = localeMapRef.current[locale] || [];
			slugMapRef.current[locale] = slugMapRef.current[locale] || {};
			localeMapRef.current[locale].push(item);
			slugMapRef.current[locale][slug] = item;
			uuidMapRef.current[uuid] = item;
			allRef.current.push(item);
		}
	}

	return {
		items: localeMapRef.current[locale],
		slugMap: slugMapRef.current[locale],
		uuidMap: uuidMapRef.current,
	};
};

const query = graphql`
	query useUseCases {
		allMarkdownRemark(
			filter: { frontmatter: { type: { eq: "use_case" } } }
			sort: { frontmatter: { name: ASC } }
		) {
			edges {
				node {
					frontmatter {
						uuid
						name
						image {
							file {
								childImageSharp {
									gatsbyImageDataFixed: gatsbyImageData(
										width: 368
										height: 245
										layout: FIXED
										transformOptions: { cropFocus: ENTROPY }
									)
									gatsbyImageDataFluid: gatsbyImageData(layout: CONSTRAINED, width: 575)
								}
							}
							title
							alt
						}
					}
					fields {
						locale
					}
				}
			}
		}
	}
`;
